/**
 * The 可提 chip: Available for Pickup Containers that carry a pickup appointment,
 * and then one day - or a span of them - at a time (Alan, 2026-09-26: "可提柜子
 * 那里所有有预约的过滤出来，能细到一天一天的过滤更好"; 2026-09-28: "这些按钮改成按钮
 * 就是可以概括任何选择的日期不只是这样子一个个的日期" - a chosen span, not one day
 * clicked at a time).
 *
 * It asks nothing new of the server's filter. The dashboard query already takes
 * an Ocean Status list and a date range on any dated column, and together they
 * are exactly this: status available_for_pickup, dated on pickup_appointment_at,
 * over all time for the chip, and over one Los Angeles day or a run of them for
 * a selection. What the server adds is the count - total and per day - from the
 * fleet walk the other chips' numbers come from, so a number on the bar is the
 * rows it shows.
 *
 * A span is picked two clicks, not a drag - the strip is a row of buttons, and a
 * click is the one gesture every input this runs on (mouse, touch) agrees on. The
 * first click on a day starts a fresh single-day selection; a second click on a
 * DIFFERENT day while that selection is still just the one day extends it to the
 * closed range between them (see onAppointmentDay in app.js, the only place that
 * decides this - everything here just reads a selection, never advances one).
 * Clicking a day once a range is already complete starts over at that one day,
 * so the cycle is always: pick one, pick another to span it, pick any one to
 * start again.
 *
 * Days are Los Angeles days on both sides. The operation runs on the terminal's
 * clock, and a browser elsewhere cutting the day at its own midnight would put a
 * 23:30 appointment on the wrong day and disagree with the count.
 */

export const APPOINTMENT_STATUS = 'available_for_pickup';
export const APPOINTMENT_FIELD = 'pickup_appointment_at';
const ALL = 'all';
const LA = 'America/Los_Angeles';

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

const LA_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: LA, year: 'numeric', month: '2-digit', day: '2-digit',
});

/** The Los Angeles day of a stored date - a Date, millis or an ISO string - or ''. */
export function laDayKey(value) {
  const millis = value instanceof Date ? value.getTime()
    : typeof value === 'number' ? value
      : typeof value === 'string' && value ? Date.parse(value) : NaN;
  return Number.isFinite(millis) && millis > 0 ? LA_DAY.format(new Date(millis)) : '';
}

/** How many rows carry a date on each Los Angeles day. */
export function rowsByDay(rows, field) {
  const days = {};
  for (const row of Array.isArray(rows) ? rows : []) {
    const day = laDayKey(row?.[field]);
    if (day) days[day] = (days[day] ?? 0) + 1;
  }
  return days;
}

const LA_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: LA, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
});

// Minutes Los Angeles is ahead of UTC at this instant: -420 in summer, -480 in winter.
function laOffsetMinutes(millis) {
  const parts = Object.fromEntries(LA_PARTS.formatToParts(new Date(millis)).map(({ type, value }) => [type, value]));
  const asUtc = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour), Number(parts.minute), Number(parts.second));
  return Math.round((asUtc - millis) / 60_000);
}

function laMidnight(isoDay) {
  const [year, month, day] = isoDay.split('-').map(Number);
  const utcMidnight = Date.UTC(year, month - 1, day);
  // The offset at UTC midnight is the previous LA evening's, and a clock change
  // happens at 2am - so it is the offset LA midnight itself is on.
  return utcMidnight - laOffsetMinutes(utcMidnight) * 60_000;
}

/** A Los Angeles calendar day as [first millisecond, last millisecond]. */
export function appointmentDayRange(isoDay) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(isoDay ?? ''))) return null;
  const [year, month, day] = isoDay.split('-').map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10);
  return [laMidnight(isoDay), laMidnight(next) - 1];
}

// A selection is 'all', one Los Angeles day ("2026-08-04"), or a closed range of
// them ("2026-08-04..2026-08-10") - joined with this separator rather than made
// an object, so every existing `filterState.appointment` truthy/string check
// (is it set, is it 'all', show it) keeps working unchanged (Alan, 2026-09-28:
// "这些按钮改成按钮就是可以概括任何选择的日期不只是这样子一个个的日期" - the day strip
// should cover a chosen SPAN, not just one day picked at a time).
const RANGE_SEPARATOR = '..';

/** A day-or-range selection as its two ends, earliest first - null for 'all'/off. */
export function appointmentSelectionBounds(selection) {
  if (!selection || selection === ALL) return null;
  const [from, to = from] = String(selection).split(RANGE_SEPARATOR);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return null;
  return from <= to ? { from, to } : { from: to, to: from };
}

/** Two days joined into one selection - a bare day when they are the same one. */
export function appointmentSelectionOf(dayA, dayB) {
  const [from, to] = [dayA, dayB].sort();
  return from === to ? from : `${from}${RANGE_SEPARATOR}${to}`;
}

/** The range read back as a short label for a sentence, e.g. "8/4" or "8/4–8/10". */
export function appointmentSelectionLabel(selection) {
  const bounds = appointmentSelectionBounds(selection);
  if (!bounds) return '';
  const short = (isoDay) => { const [, month, day] = isoDay.split('-'); return `${Number(month)}/${Number(day)}`; };
  return bounds.from === bounds.to ? short(bounds.from) : `${short(bounds.from)}–${short(bounds.to)}`;
}

/**
 * What the chip adds to the dashboard query: null when it is off, all time for
 * the chip, the whole closed range - start of its first day to end of its last -
 * for a day or a span of them.
 */
export function appointmentQuery(selection) {
  if (!selection) return null;
  if (selection === ALL) {
    return {
      statuses: [APPOINTMENT_STATUS],
      dateField: APPOINTMENT_FIELD,
      fromMillis: 0,
      toMillis: Number.MAX_SAFE_INTEGER,
    };
  }
  const bounds = appointmentSelectionBounds(selection);
  const start = bounds && appointmentDayRange(bounds.from);
  const end = bounds && appointmentDayRange(bounds.to);
  if (!start || !end) return null;
  return {
    statuses: [APPOINTMENT_STATUS],
    dateField: APPOINTMENT_FIELD,
    fromMillis: start[0],
    toMillis: end[1],
  };
}

const WEEKDAYS = Object.freeze({
  zh: ['周日', '周一', '周二', '周三', '周四', '周五', '周六'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
});

function dayLabel(isoDay, zh) {
  const [year, month, day] = isoDay.split('-').map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  const names = zh ? WEEKDAYS.zh : WEEKDAYS.en;
  return `${month}/${day} ${names[weekday]}`;
}

/**
 * The day strip under the bar, shown while the chip is on: 全部, then every day
 * that has an appointment, soonest first, each with its count. Every day inside
 * a selected span shows pressed, not only its two ends, so a range reads as one
 * continuous choice rather than two isolated picks.
 */
export function appointmentDaysMarkup({ days = {}, total = 0, selected = ALL, locale = 'zh' } = {}) {
  const zh = String(locale).toLowerCase().startsWith('zh');
  const bounds = appointmentSelectionBounds(selected);
  const pressed = (value) => (value === ALL ? selected === ALL : Boolean(bounds && value >= bounds.from && value <= bounds.to));
  const chip = (value, label, count) => `<button type="button" class="dashboard-appointment-day" data-appointment-day="${escapeHtml(value)}" aria-pressed="${pressed(value)}"><span>${escapeHtml(label)}</span><span class="dashboard-appointment-day__count">${escapeHtml(count)}</span></button>`;
  const dated = Object.keys(days ?? {})
    .filter((key) => /^\d{4}-\d{2}-\d{2}$/.test(key) && Number(days[key]) > 0)
    .sort();
  return [
    chip(ALL, zh ? '全部' : 'All', total),
    ...dated.map((key) => chip(key, dayLabel(key, zh), days[key])),
  ].join('');
}
